import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, DollarSign, Plus, Send, Check, X, RefreshCw, ExternalLink, Trash2, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/** Owner-facing panel to invoice private-pay parents. Lives inside
 *  /dashboard/billing (behind the same passcode). Money flows directly to
 *  the owner's PayPal.me — we never touch it. iter-77. */
export default function ParentInvoicesPanel() {
  const [cfg, setCfg] = useState({ paypal_me_handle: "", paypal_business_email: "", fee_catalog: [] });
  const [busy, setBusy] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [children, setChildren] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  const loadAll = () => Promise.all([
    api.get("/center/parent-billing").then((r) => setCfg(r.data)).catch(() => {}),
    api.get("/parent-invoices").then((r) => setInvoices(r.data)).catch(() => {}),
    api.get("/children").then((r) => setChildren(r.data)).catch(() => {}),
  ]);

  useEffect(() => { loadAll(); }, []);

  const filtered = invoices.filter((i) => statusFilter === "all" || i.status === statusFilter);
  const totals = {
    unpaid: invoices.filter((i) => i.status === "issued").reduce((s, i) => s + (i.amount || 0), 0),
    paid_ytd: invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (i.amount || 0), 0),
    count_issued: invoices.filter((i) => i.status === "issued").length,
  };

  const needsSetup = !cfg.paypal_me_handle;
  const paypalUrl = cfg.paypal_me_handle ? `https://paypal.me/${cfg.paypal_me_handle}` : null;

  const markPaid = async (inv) => {
    if (!window.confirm(`Mark invoice ${inv.invoice_number} for $${inv.amount.toFixed(2)} as PAID?\n\nA receipt PDF will be emailed to ${inv.parent_email}.`)) return;
    setBusy(true);
    try {
      await api.post(`/parent-invoices/${inv.id}/mark-paid`, { paid_notes: null, send_receipt: true });
      toast.success(`Marked paid — receipt queued for ${inv.parent_email}`);
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const voidInvoice = async (inv) => {
    if (!window.confirm(`Void invoice ${inv.invoice_number}? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.post(`/parent-invoices/${inv.id}/void`);
      toast.success(`Voided ${inv.invoice_number}`);
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const resend = async (inv) => {
    // iter-81q — offer explicit choice: full resend (with email) or silent
    // re-publish (record only). Fixes complaint that every republish
    // clicked automatically fires a parent email.
    const emailIt = window.confirm(
      `Also email ${inv.parent_email}?\n\nOK  = republish AND email\nCancel = republish silently (no email)`
    );
    setBusy(true);
    try {
      await api.post(`/parent-invoices/${inv.id}/resend?notify=${emailIt ? "true" : "false"}`);
      toast.success(emailIt ? `Queued — resending to ${inv.parent_email}` : "Invoice re-published silently (no email).");
      await loadAll();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/parent-invoices/${inv.id}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section data-testid="parent-invoices-panel" className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
      <div className="p-5 border-b border-stone-200 flex items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Private-pay tuition</div>
          <h2 className="mt-1 font-display text-2xl font-medium text-stone-900">Parent invoices</h2>
          <p className="mt-1 text-sm text-stone-500 max-w-2xl">
            Bill families for tuition, late fees, activity fees, and one-off charges. Parents pay via <b>PayPal.me</b> — funds go straight to your PayPal account. Mark as paid to send a receipt automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="parent-invoices-setup-btn"
            variant="outline"
            onClick={() => setShowSetup(true)}
            className="rounded-xl h-10"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Setup
          </Button>
          <Button
            data-testid="parent-invoices-new-btn"
            onClick={() => needsSetup ? setShowSetup(true) : setShowNew(true)}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-10"
          >
            <Plus className="w-4 h-4 mr-1" /> New invoice
          </Button>
        </div>
      </div>

      {needsSetup && (
        <div className="p-5 bg-amber-50 border-b border-amber-200 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 grid place-items-center flex-shrink-0">
            <DollarSign className="w-4 h-4 text-amber-800" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-amber-900">Connect your PayPal to start invoicing parents</div>
            <p className="text-sm text-amber-900/80 mt-0.5">Add your PayPal.me handle (e.g. <code className="bg-amber-100 px-1 rounded">paypal.me/yourcentername</code>) so parents can pay you directly from every invoice.</p>
          </div>
          <Button size="sm" onClick={() => setShowSetup(true)} className="bg-amber-800 hover:bg-amber-900 text-white rounded-lg">Connect PayPal →</Button>
        </div>
      )}

      {/* Stats + filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5 border-b border-stone-100">
        <StatMini label="Outstanding" value={`$${totals.unpaid.toFixed(2)}`} sub={`${totals.count_issued} invoice${totals.count_issued === 1 ? "" : "s"}`} tone="amber" />
        <StatMini label="Collected (all-time)" value={`$${totals.paid_ytd.toFixed(2)}`} sub={`${invoices.filter((i) => i.status === "paid").length} receipts`} tone="emerald" />
        <StatMini label="PayPal handle" value={cfg.paypal_me_handle ? `@${cfg.paypal_me_handle}` : "—"} sub={paypalUrl ? "Configured" : "Not set"} tone={paypalUrl ? "emerald" : "stone"} />
        <StatMini label="Fee catalog" value={`${(cfg.fee_catalog || []).length} items`} sub={(cfg.fee_catalog || []).length === 0 ? "Add presets in Setup" : "Ready to invoice"} tone="stone" />
      </div>

      {/* Filter tabs */}
      <div className="px-5 py-3 border-b border-stone-100 flex gap-2 text-sm">
        {[["all", "All"], ["issued", "Unpaid"], ["paid", "Paid"], ["void", "Voided"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`parent-invoices-filter-${k}`}
            onClick={() => setStatusFilter(k)}
            className={`px-3 py-1.5 rounded-lg font-medium transition-colors ${
              statusFilter === k ? "bg-emerald-700 text-white" : "text-stone-600 hover:bg-stone-100"
            }`}
          >
            {l} {k !== "all" && invoices.filter((i) => i.status === k).length > 0 && (
              <span className="ml-1 text-xs opacity-80">({invoices.filter((i) => i.status === k).length})</span>
            )}
          </button>
        ))}
      </div>

      {/* Invoice list */}
      <div className="divide-y divide-stone-100">
        {filtered.length === 0 && (
          <div className="p-10 text-center text-stone-500">
            <FileText className="w-8 h-8 mx-auto text-stone-300" />
            <p className="mt-3 text-sm">No invoices yet in this view.</p>
          </div>
        )}
        {filtered.map((inv) => (
          <div key={inv.id} data-testid={`parent-invoice-row-${inv.id}`} className="p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 hover:bg-stone-50">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-stone-500">{inv.invoice_number}</span>
                <StatusPill status={inv.status} />
                {inv.email_status === "failed" && (
                  <span className="text-[10px] uppercase tracking-widest bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Email failed</span>
                )}
              </div>
              <div className="mt-1 font-medium text-stone-900 truncate">
                {inv.child_name || "—"} <span className="text-stone-400">·</span> <span className="text-stone-600">{inv.parent_name}</span>
              </div>
              <div className="text-xs text-stone-500 truncate">{inv.parent_email} · {inv.period_label || "one-off"}</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-lg font-semibold text-stone-900">${inv.amount.toFixed(2)}</div>
              <div className="text-[11px] text-stone-400">
                Issued {new Date(inv.issued_at).toLocaleDateString()}
                {inv.status === "paid" && inv.paid_at && ` · Paid ${new Date(inv.paid_at).toLocaleDateString()}`}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={() => downloadPdf(inv)} className="h-9 rounded-lg" data-testid={`parent-invoice-pdf-${inv.id}`}>
                <Download className="w-3.5 h-3.5" />
              </Button>
              {inv.status === "issued" && (
                <>
                  <Button size="sm" variant="outline" onClick={() => resend(inv)} disabled={busy} className="h-9 rounded-lg" data-testid={`parent-invoice-resend-${inv.id}`}>
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" onClick={() => markPaid(inv)} disabled={busy} className="h-9 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`parent-invoice-mark-paid-${inv.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Mark paid
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => voidInvoice(inv)} disabled={busy} className="h-9 rounded-lg border-red-200 text-red-700 hover:bg-red-50" data-testid={`parent-invoice-void-${inv.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
              {inv.status === "paid" && (
                <Button size="sm" variant="outline" onClick={() => resend(inv)} disabled={busy} className="h-9 rounded-lg text-emerald-700 border-emerald-200" data-testid={`parent-invoice-resend-receipt-${inv.id}`}>
                  <Send className="w-3.5 h-3.5 mr-1" /> Resend receipt
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {showSetup && (
        <SetupDialog
          initial={cfg}
          onClose={() => setShowSetup(false)}
          onSaved={async () => { await loadAll(); setShowSetup(false); }}
        />
      )}
      {showNew && (
        <NewInvoiceDialog
          catalog={cfg.fee_catalog || []}
          childrenList={children}
          paypalHandle={cfg.paypal_me_handle}
          onClose={() => setShowNew(false)}
          onCreated={async () => { await loadAll(); setShowNew(false); }}
        />
      )}
    </section>
  );
}

const StatusPill = ({ status }) => {
  const map = {
    issued: ["bg-amber-100 text-amber-800", "Unpaid"],
    paid: ["bg-emerald-100 text-emerald-800", "Paid"],
    void: ["bg-stone-200 text-stone-700", "Voided"],
  };
  const [cls, label] = map[status] || ["bg-stone-100 text-stone-700", status];
  return <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
};

const StatMini = ({ label, value, sub, tone = "stone" }) => {
  const bg = { emerald: "bg-emerald-50", amber: "bg-amber-50", stone: "bg-stone-50" }[tone] || "bg-stone-50";
  return (
    <div className={`rounded-xl border border-stone-200 p-3 ${bg}`}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{label}</div>
      <div className="mt-1 font-mono text-base font-semibold text-stone-900 truncate">{value}</div>
      <div className="text-[11px] text-stone-500">{sub}</div>
    </div>
  );
};

// ----- Setup dialog: PayPal.me handle + business email + fee catalog CRUD -----
function SetupDialog({ initial, onClose, onSaved }) {
  const [handle, setHandle] = useState(initial.paypal_me_handle || "");
  const [email, setEmail] = useState(initial.paypal_business_email || "");
  const [catalog, setCatalog] = useState(initial.fee_catalog || []);
  const [saving, setSaving] = useState(false);

  const addRow = () => setCatalog((c) => [...c, { id: "", name: "", default_amount: 0, description: "" }]);
  const updateRow = (i, k, v) => setCatalog((c) => c.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const removeRow = (i) => setCatalog((c) => c.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/center/parent-billing", { paypal_me_handle: handle, paypal_business_email: email || null });
      const cleaned = catalog
        .filter((r) => r.name.trim())
        .map((r) => ({ id: r.id || undefined, name: r.name.trim(), default_amount: Number(r.default_amount) || 0, description: (r.description || "").trim() || null }));
      await api.put("/center/parent-billing/fee-catalog", cleaned);
      toast.success("Parent-billing settings saved");
      await onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <h3 className="font-display text-xl text-stone-900">Parent billing setup</h3>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-widest text-emerald-700 font-bold">PayPal.me handle</Label>
            <Input
              data-testid="parent-billing-paypal-handle"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="yourcentername (or full paypal.me URL)"
              className="mt-2"
            />
            <p className="text-xs text-stone-500 mt-1">This is what parents click to pay you. Get yours at <a className="text-emerald-700 font-semibold" href="https://paypal.me" target="_blank" rel="noreferrer">paypal.me →</a> (needs a PayPal Business account).</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-emerald-700 font-bold">PayPal business email (optional)</Label>
            <Input
              data-testid="parent-billing-paypal-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="billing@yourcenter.com"
              className="mt-2"
            />
            <p className="text-xs text-stone-500 mt-1">Shown on the invoice footer as backup contact.</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Fee catalog</Label>
              <Button size="sm" variant="outline" onClick={addRow} className="h-8 rounded-lg" data-testid="parent-billing-fee-add">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add fee
              </Button>
            </div>
            <div className="space-y-2">
              {catalog.length === 0 && (
                <p className="text-xs text-stone-400 italic">No preset fees yet. Add &ldquo;Weekly Tuition&rdquo;, &ldquo;Late Pickup Fee&rdquo;, etc. so you can invoice in one click.</p>
              )}
              {catalog.map((row, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start">
                  <Input data-testid={`parent-billing-fee-name-${i}`} className="col-span-6 h-9" value={row.name} onChange={(e) => updateRow(i, "name", e.target.value)} placeholder="Weekly Tuition (Infant)" />
                  <Input data-testid={`parent-billing-fee-amount-${i}`} className="col-span-3 h-9 font-mono" type="number" step="0.01" value={row.default_amount} onChange={(e) => updateRow(i, "default_amount", e.target.value)} placeholder="275" />
                  <Input data-testid={`parent-billing-fee-desc-${i}`} className="col-span-2 h-9" value={row.description || ""} onChange={(e) => updateRow(i, "description", e.target.value)} placeholder="notes" />
                  <Button variant="ghost" size="sm" className="col-span-1 h-9 text-red-600" onClick={() => removeRow(i)} data-testid={`parent-billing-fee-remove-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-stone-200 flex items-center justify-end gap-2 bg-stone-50">
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button data-testid="parent-billing-save-btn" onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Save settings
          </Button>
        </div>
      </div>
    </div>
  );
}

// ----- New invoice dialog: pick child, add fees from catalog, set period ----
function NewInvoiceDialog({ catalog, childrenList, paypalHandle, onClose, onCreated }) {
  const [childId, setChildId] = useState("");
  const [items, setItems] = useState([]);
  const [period, setPeriod] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const child = childrenList.find((c) => c.id === childId);
  const total = items.reduce((s, it) => s + (Number(it.quantity) || 1) * (Number(it.unit_price) || 0), 0);
  const canSend = childId && items.length > 0 && total > 0 && !!child?.parent_email;

  const addFromCatalog = (fee) => {
    setItems((arr) => [...arr, {
      description: fee.name,
      quantity: 1,
      unit_price: Number(fee.default_amount) || 0,
      catalog_id: fee.id,
    }]);
  };
  const updateItem = (i, k, v) => setItems((arr) => arr.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!canSend) return;
    setSaving(true);
    try {
      const res = await api.post("/parent-invoices", {
        child_id: childId,
        line_items: items.map((it) => ({
          description: it.description,
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
          catalog_id: it.catalog_id || null,
        })),
        period_label: period || null,
        due_date: dueDate || null,
        notes: notes || null,
        send_email: true,
      });
      toast.success(`Invoice ${res.data.invoice_number} queued for ${res.data.parent_email}`);
      await onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-stone-200 flex items-center justify-between">
          <h3 className="font-display text-xl text-stone-900">New parent invoice</h3>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Child</Label>
            <select
              data-testid="parent-invoice-child-picker"
              value={childId}
              onChange={(e) => setChildId(e.target.value)}
              className="mt-2 w-full rounded-lg border border-stone-300 h-11 px-3 focus:ring-emerald-600 focus:border-emerald-600"
            >
              <option value="">Choose a child…</option>
              {childrenList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name} — {c.parent_name || "no parent name"} ({c.parent_email || "NO EMAIL"})
                </option>
              ))}
            </select>
            {child && !child.parent_email && (
              <p className="mt-1 text-xs text-red-600">⚠ This child has no parent email — add one on their profile before invoicing.</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Line items</Label>
              {catalog.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {catalog.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => addFromCatalog(f)}
                      data-testid={`parent-invoice-catalog-${f.id}`}
                      className="text-[11px] rounded-full px-2.5 py-1 border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 font-medium"
                    >
                      + {f.name} <span className="font-mono">${Number(f.default_amount).toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              {items.length === 0 && (
                <p className="text-xs text-stone-400 italic">
                  {catalog.length === 0
                    ? "No fee presets configured. Click Setup first to define your fee catalog."
                    : "Click a preset above to add a line item."}
                </p>
              )}
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start">
                  <Input data-testid={`parent-invoice-item-desc-${i}`} className="col-span-7 h-9" value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} />
                  <Input data-testid={`parent-invoice-item-qty-${i}`} className="col-span-1 h-9 font-mono text-center" type="number" step="1" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} />
                  <Input data-testid={`parent-invoice-item-price-${i}`} className="col-span-3 h-9 font-mono" type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} />
                  <Button variant="ghost" size="sm" className="col-span-1 h-9 text-red-600" onClick={() => removeItem(i)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Period (optional)</Label>
              <Input data-testid="parent-invoice-period" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Week of Jul 6, 2026" className="mt-2 h-10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Due date (optional)</Label>
              <Input data-testid="parent-invoice-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-2 h-10" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Notes (optional)</Label>
            <Input data-testid="parent-invoice-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add a friendly note" className="mt-2 h-10" />
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-emerald-800 font-bold">Total to invoice</div>
            <div className="font-mono text-2xl font-bold text-emerald-900">${total.toFixed(2)}</div>
          </div>
          {paypalHandle && (
            <p className="text-xs text-stone-500 flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Parent will be sent to <b>paypal.me/{paypalHandle}/{total.toFixed(2)}USD</b>
            </p>
          )}
        </div>
        <div className="p-6 border-t border-stone-200 flex items-center justify-end gap-2 bg-stone-50">
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button data-testid="parent-invoice-send-btn" onClick={submit} disabled={!canSend || saving} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send invoice to parent
          </Button>
        </div>
      </div>
    </div>
  );
}
